/**
 * M2MD Communications adapter — IOT-VENDOR-M2MD-1.
 *
 * Vehicle telematics integration via M2MD embedded cellular module (OBD-II / CAN bus).
 * Provides GPS, speed, odometer, engine RPM, fuel level, battery voltage.
 *
 * Integration level: STUB — telemetry ingestion only.
 * Vehicle commands (remote diagnostics, geofence config) deferred to IOT-VENDOR-M2MD-2.
 *
 * HAL-WRITE-PATH-DOCTRINE-1: adapter does NOT write telemetry directly.
 */

import { NotImplementedAdapter } from '../NotImplementedAdapter.js';
import { VendorNotIntegratedError } from '../VendorNotIntegratedError.js';

const M2MD_CAPABILITIES = {
  configKeys: {
    reporting_interval_sec: {
      pushable: true,
      readable: true,
      range: [30, 3600],
      requiresActivation: false,
      unit: 'seconds',
      description: 'Telematics reporting interval',
    },
    speed_alert_kph: {
      pushable: true,
      readable: true,
      range: [0, 200],
      requiresActivation: false,
      unit: 'km/h',
      description: 'Speed alert threshold',
    },
  },
  telemetryTypes: [
    'gps_position',
    'speed_kph',
    'odometer_km',
    'engine_rpm',
    'fuel_level_pct',
    'battery_voltage_v',
  ],
  alertingMode: 'cloud',
  otaSupported: false,
  attestationSupported: false,
  rollbackSupported: false,
  pushLatencyExpectedMs: 15_000,
};

export class M2MDAdapter extends NotImplementedAdapter {
  /**
   * @param {import('../../contracts/DeviceContract.ts').DeviceRecord} device
   */
  constructor(device) {
    super(device, 'm2md', M2MD_CAPABILITIES);

    /** @type {import('../../contracts/DeviceContract.ts').NormalizedReading[]} */
    this._readings = [];
    /** @type {Array<import('../../contracts/DeviceContract.ts').TelemetryHandler>} */
    this._telemetrySubscribers = [];
  }

  // ── Telemetry — STUB implementation ─────────────────────────────────────────

  /**
   * Feed a normalized reading into the adapter (called by iotIngest CF).
   * Notifies all live subscribers.
   *
   * @param {import('../../contracts/DeviceContract.ts').NormalizedReading} reading
   */
  ingestReading(reading) {
    this._readings.push(reading);
    for (const handler of this._telemetrySubscribers) {
      try { handler(reading); } catch { /* subscriber errors must not crash ingest */ }
    }
  }

  /**
   * Return readings for the requested time window and sensor types.
   *
   * @param {import('../../contracts/DeviceContract.ts').TimeWindow} window
   * @param {string[]} [sensorTypes]
   */
  async getTelemetry(window, sensorTypes) {
    let readings = this._readings;
    if (window) {
      const from = new Date(window.from).getTime();
      const to   = new Date(window.to).getTime();
      readings = readings.filter((r) => {
        const ts = new Date(r.timestamp).getTime();
        return ts >= from && ts <= to;
      });
    }
    if (sensorTypes?.length) {
      readings = readings.filter((r) => sensorTypes.includes(r.sensorType));
    }
    return readings;
  }

  subscribeToTelemetry(handler) {
    this._telemetrySubscribers.push(handler);
    return {
      unsubscribe: () => {
        this._telemetrySubscribers = this._telemetrySubscribers.filter((h) => h !== handler);
      },
    };
  }

  // ── Config / OTA — not integrated; explicit errors per STUB level ────────────

  async pushConfig()       { throw new VendorNotIntegratedError('m2md', 'pushConfig'); }
  async getCurrentConfig() { throw new VendorNotIntegratedError('m2md', 'getCurrentConfig'); }
  async getPendingConfig() { throw new VendorNotIntegratedError('m2md', 'getPendingConfig'); }
  async rollbackConfig()   { throw new VendorNotIntegratedError('m2md', 'rollbackConfig'); }
  async getDeviceHealth()  { throw new VendorNotIntegratedError('m2md', 'getDeviceHealth'); }
  subscribeToEvents()      { throw new VendorNotIntegratedError('m2md', 'subscribeToEvents'); }
}

/**
 * Factory for HalService.registerAdapter('m2md', createM2MDAdapterFactory()).
 */
export function createM2MDAdapterFactory() {
  return (device) => new M2MDAdapter(device);
}
